import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(null);
  const [originalAdmin, setOriginalAdmin] = useState(null);
  const [credits, setCredits] = useState(0);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Axios interceptor to add auth token
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/auth/me`);
          setUser(response.data);
          setCredits(response.data.credits || 0);
          
          // Check for impersonation data
          const impersonatingData = localStorage.getItem('impersonating');
          const originalAdminData = localStorage.getItem('original_admin');
          
          if (impersonatingData && originalAdminData) {
            setImpersonating(JSON.parse(impersonatingData));
            setOriginalAdmin(JSON.parse(originalAdminData));
          }
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('impersonating');
          localStorage.removeItem('original_admin');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [API_BASE_URL]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email,
        password,
      });
      
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      
      // Get user info
      const userResponse = await axios.get(`${API_BASE_URL}/api/auth/me`);
      setUser(userResponse.data);
      setCredits(userResponse.data.credits || 0);
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Login failed' 
      };
    }
  };

  const loginWithGoogle = async (idToken) => {
    try {
      // Call backend to exchange Google ID token for JWT token
      const response = await axios.post(`${API_BASE_URL}/api/auth/google`, {
        session_id: idToken,  // Backend expects session_id but we're sending ID token
      });
      
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      
      // Get user info
      const userResponse = await axios.get(`${API_BASE_URL}/api/auth/me`);
      setUser(userResponse.data);
      setCredits(userResponse.data.credits || 0);
      
      return { success: true };
    } catch (error) {
      console.error('Google login error:', error);
      console.error('Error response:', error.response?.data);
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Google login failed' 
      };
    }
  };

  const register = async (email, password, fullName) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/register`, {
        email,
        password,
        full_name: fullName,
      });
      
      // Auto login after registration
      const loginResult = await login(email, password);
      return loginResult;
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('original_admin');
    localStorage.removeItem('original_admin_token');
    setUser(null);
    setImpersonating(null);
    setOriginalAdmin(null);
  };

  const refreshCredits = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/credits/balance`);
      setCredits(response.data.balance);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh credits:', error);
      return null;
    }
  };

  const checkCreditsForOperation = async (operation) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/credits/check/${operation}`);
      return response.data;
    } catch (error) {
      console.error('Failed to check credits:', error);
      return { sufficient: false, error: 'Failed to check credits' };
    }
  };

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    register,
    logout,
    API_BASE_URL,
    impersonating,
    originalAdmin,
    credits,
    refreshCredits,
    checkCreditsForOperation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};